import type { UpdateInvoiceTitleReq } from '@/api/type-request'
import { FEE, FORM_VALIDATION } from '@/consts/billing'
import type { CharityInfo } from '@/types/billing'

export const validateEmail = (email: string): boolean => {
  return FORM_VALIDATION.EMAIL_REGEX.test(email)
}

export const formatPaymentAmount = (
  amount: number,
  currency: string,
  isCalculating: boolean,
): string => {
  if (isCalculating) {
    return 'Calculating...'
  }
  if (amount > 0) {
    return `${amount.toFixed(2)} ${currency}`
  }
  return '0.00'
}

export const autoCorrectFee = (value: number): number => {
  const roundedValue = Math.ceil(value / 10) * 10
  return roundedValue <= FEE.MAXIMUM ? roundedValue : FEE.MAXIMUM
}

export const isValidFeeAmount = (fee: number): boolean => {
  return fee >= FEE.MINIMUM && fee <= FEE.MAXIMUM
}

export const validateBusinessId = (businessId: string): boolean => {
  if (!businessId || !businessId.trim()) return false

  // Must be exactly 8 digits
  const digitRegex = /^\d{8}$/
  return digitRegex.test(businessId)
}

export const validateMobileBarcode = (mobileBarcode: string): boolean => {
  if (!mobileBarcode || !mobileBarcode.trim()) return false

  // Must start with '/' followed by 7 characters from the MOF e-invoice
  // carrier charset: digits, uppercase letters, and '+', '-', '.'
  return FORM_VALIDATION.MOBILE_BARCODE_REGEX.test(mobileBarcode)
}

export const validateInvoiceForm = (
  invoiceType: string,
  formValues: {
    name?: string
    email?: string
    mobileBarcode?: string
    businessId?: string
    businessName?: string
    charityCode?: string
  },
): boolean => {
  const { name, email, mobileBarcode, businessId, businessName, charityCode } = formValues

  switch (invoiceType) {
    case 'electronic':
      return Boolean(name && name.trim() && email && email.trim() && validateEmail(email))

    case 'mobile-barcode':
      return Boolean(
        name &&
          name.trim() &&
          mobileBarcode &&
          mobileBarcode.trim() &&
          validateMobileBarcode(mobileBarcode),
      )

    case 'business-id':
    case 'unified-business':
      return Boolean(
        businessId &&
          validateBusinessId(businessId) &&
          businessName &&
          businessName.trim() &&
          email &&
          email.trim() &&
          validateEmail(email),
      )

    case 'donation':
      return Boolean(name && name.trim() && charityCode && charityCode.trim())

    default:
      return false
  }
}

export const transformInvoiceFormToApiData = (
  invoiceType: string,
  formValues: any,
  selectedCharity?: CharityInfo,
): UpdateInvoiceTitleReq['ezpay'] => {
  switch (invoiceType) {
    case 'mobile-barcode':
      return {
        name: formValues.name || '',
        type: 1,
        carrier_type: 1,
        carrier_num: formValues.mobileBarcode,
      }

    case 'unified-business': {
      // If businessName exceeds 60 characters, use businessId instead
      const nameForApi =
        formValues.businessName && formValues.businessName.length > 60
          ? formValues.businessId
          : formValues.businessName || ''

      return {
        name: nameForApi,
        type: 2,
        carrier_type: 3,
        UBN: formValues.businessId,
        email: formValues.email,
      }
    }

    case 'donation':
      return {
        name: formValues.name || '',
        type: 1,
        carrier_type: 0,
        donate_code: formValues.charityCode || selectedCharity?.code,
      }

    case 'electronic':
    default:
      return {
        name: formValues.name || '',
        type: 1,
        carrier_type: 3,
        email: formValues.email,
      }
  }
}
